import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useInsights } from '../hooks/useInsights';

const SERVICE_INFO = {
  'LinkedIn_Automation': { icon: '🔗', short: 'LinkedIn', desc: 'Finding and connecting with target prospects on LinkedIn' },
  'Email_Marketing':     { icon: '📧', short: 'Email', desc: 'Running targeted email campaigns to reach potential customers' },
  'Content_Creation':    { icon: '✍️', short: 'Content', desc: 'Writing blogs, newsletters, and SEO content' },
  'SEO':                 { icon: '🔍', short: 'SEO', desc: 'Improving search engine rankings and organic traffic' },
  'BD_for_Clients':      { icon: '🤝', short: 'Business Dev', desc: 'Finding and introducing potential partners or customers' },
  'Retention_Marketing': { icon: '🔄', short: 'Retention', desc: 'Keeping existing customers engaged and reducing churn' },
  'Podcast_&_Video_Production': { icon: '🎬', short: 'Podcast & Video', desc: 'Producing podcast episodes and video content' },
  'Website_UI/UX_Design':      { icon: '🎨', short: 'Website Design', desc: 'Designing website layouts and user experience' },
  'Website_Development':        { icon: '💻', short: 'Website Dev', desc: 'Building and maintaining websites' },
  'Performance_Marketing':      { icon: '📈', short: 'Paid Ads', desc: 'Running paid advertising campaigns for leads and sales' },
  'Media_&_PR':                 { icon: '📰', short: 'Media & PR', desc: 'Getting press coverage and managing media relations' },
  'Logo_&_Brand_Guidelines':   { icon: '🏷️', short: 'Branding', desc: 'Creating brand identity, logos, and visual guidelines' },
  'Creative_&_Video':           { icon: '🎥', short: 'Creative & Video', desc: 'Creating ad creatives, videos, and visual assets' },
  'Outsourced_Head_of_Operations': { icon: '📋', short: 'Operations', desc: 'Managing day-to-day operations and processes' }
};

const STATUS = {
  'On Track':  { icon: '✅', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', soft: '#dcfce7' },
  'At Risk':   { icon: '⚠️', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', soft: '#ffedd5' },
  'Delayed':   { icon: '🔴', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', soft: '#fee2e2' },
  'Unknown':   { icon: '⚪', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', soft: '#f3f4f6' }
};

const FALSE_BLOCKERS = new Set(['on schedule — no issues','on schedule - no issues','on schedule','no issues','none','n/a','na','-','','no blockers','no blockers at the moment','no current blockers']);

function norm(s) {
  if (!s) return 'Unknown';
  const l = s.toLowerCase();
  if (l.includes('track')) return 'On Track';
  if (l.includes('risk') || l.includes('off track')) return 'At Risk';
  if (l.includes('delay') || l.includes('blocked')) return 'Delayed';
  return 'Unknown';
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Build service sections with real data ────────────────────────────────
function buildServices(history) {
  if (!history || !history.length) return [];
  const byForm = {};
  for (const h of history) {
    if (!byForm[h.form]) byForm[h.form] = [];
    byForm[h.form].push(h);
  }
  return Object.entries(byForm).map(([form, entries]) => {
    const info = SERVICE_INFO[form] || { icon: '📌', short: form, desc: form };
    const latest = entries[0];
    const rc = latest.richContent || {};

    let concrete = '';
    if (form === 'BD_for_Clients') {
      concrete = rc.notes && rc.notes !== 'N/A' && rc.notes !== 'No updates' ? rc.notes : 'Identifying and connecting with potential business partners';
    } else if (form === 'LinkedIn_Automation') {
      concrete = rc.tool ? `LinkedIn outreach using ${rc.tool}` : 'LinkedIn outreach and lead generation';
    } else if (form === 'Email_Marketing') {
      concrete = rc.emailsSent ? `Email campaigns — ${rc.emailsSent}` : 'Targeted email campaigns';
    } else if (form === 'Content_Creation') {
      concrete = rc.publishedVsTarget ? `Content delivery: ${rc.publishedVsTarget}` : 'Blog and newsletter content creation';
    } else if (form === 'SEO') {
      concrete = 'SEO optimization and search rankings improvement';
    } else if (form === 'Retention_Marketing') {
      concrete = 'Customer retention, CRM campaigns, and churn prevention';
    } else if (form === 'Podcast_&_Video_Production') {
      concrete = rc.episodesDelivered ? `Podcast & video production — ${rc.episodesDelivered}` : 'Podcast and video content production';
    } else {
      concrete = info.desc;
    }

    let activity = '';
    if (rc.completedThisWeek && rc.completedThisWeek !== 'N/A') activity = rc.completedThisWeek;
    else if (rc.activity && rc.activity !== 'N/A' && rc.activity !== 'No activities happened') activity = rc.activity;
    else if (rc.contentDelivered && rc.contentDelivered !== 'N/A') activity = rc.contentDelivered;
    else if (rc.deliveryThisWeek && rc.deliveryThisWeek !== 'N/A') activity = rc.deliveryThisWeek;
    else if (rc.campaignsExecuted && rc.campaignsExecuted !== 'N/A') activity = rc.campaignsExecuted.slice(0, 120);

    const metrics = [];
    if (rc.emailsSent && rc.emailsSent !== 'N/A') metrics.push({ l: 'Emails sent', v: rc.emailsSent });
    if (rc.connectionRequests) metrics.push({ l: 'Connections made', v: rc.connectionRequests });
    if (rc.positiveReplies) metrics.push({ l: 'Positive replies', v: rc.positiveReplies });
    if (rc.meetingsHeld && rc.meetingsHeld !== 'N/A') metrics.push({ l: 'Meetings held', v: rc.meetingsHeld });
    if (rc.conversions && rc.conversions !== 'N/A') metrics.push({ l: 'Conversions', v: rc.conversions });
    if (rc.openRate) metrics.push({ l: 'Open rate', v: rc.openRate });
    if (rc.clickRate) metrics.push({ l: 'Click rate', v: rc.clickRate });
    if (rc.churnRiskAccounts) metrics.push({ l: 'Accounts at risk', v: rc.churnRiskAccounts });
    if (rc.publishedVsTarget && rc.publishedVsTarget !== 'N/A') metrics.push({ l: 'Delivered vs target', v: rc.publishedVsTarget });
    if (rc.episodesDelivered) metrics.push({ l: 'Episodes delivered', v: rc.episodesDelivered });
    if (rc.satisfactionSignals && rc.satisfactionSignals !== 'N/A') metrics.push({ l: 'Client sentiment', v: rc.satisfactionSignals.slice(0, 60) });
    if (rc.keywordMovements && rc.keywordMovements !== 'N/A') metrics.push({ l: 'Keyword movements', v: rc.keywordMovements });
    if (rc.renewalsUpsells && rc.renewalsUpsells !== 'N/A') metrics.push({ l: 'Renewals & upsells', v: rc.renewalsUpsells.slice(0, 80) });

    const nextPlan = (rc.planNextWeek && rc.planNextWeek !== 'N/A' && rc.planNextWeek !== 'No Plans') ? rc.planNextWeek : null;

    // Separate: what's blocked vs why it's delayed
    const isBlocked = latest.blockers && latest.blockers.trim() && !FALSE_BLOCKERS.has(latest.blockers.trim().toLowerCase());
    const blockedText = isBlocked ? latest.blockers.trim() : null;

    const hasReason = rc.flags && !FALSE_BLOCKERS.has(rc.flags.trim().toLowerCase());
    const reasonFlag = hasReason ? rc.flags.trim() : null;
    const reasonExplanation = (hasReason && rc.notes && rc.notes !== 'N/A' && rc.notes !== 'No updates') ? rc.notes.trim() : null;

    let engagement = '';
    if (rc.engagement && rc.engagement !== 'N/A') engagement = rc.engagement;
    if (rc.contentStatus && rc.contentStatus !== 'N/A') engagement = rc.contentStatus;

    return {
      form, info, entries, status: norm(latest.status),
      concrete, activity, metrics, nextPlan, engagement,
      tool: (rc.tool && rc.tool !== 'N/A') ? rc.tool : null,
      lastUpdate: latest.date, totalUpdates: entries.length,
      blockedText, reasonFlag, reasonExplanation
    };
  });
}

function buildScope(history) {
  if (!history || !history.length) return null;
  const scopeLines = [];
  const metrics = [];
  const seen = new Set();
  for (const h of history) {
    const rc = h.richContent || {};
    if (rc.scope && rc.scope !== 'N/A' && rc.scope.trim() && !seen.has('s')) { seen.add('s'); scopeLines.push(rc.scope.trim()); }
    if (rc.publishedVsTarget && rc.publishedVsTarget !== 'N/A' && !seen.has('p')) { seen.add('p'); metrics.push(rc.publishedVsTarget); }
    if (rc.episodesDelivered && !seen.has('e')) { seen.add('e'); metrics.push(rc.episodesDelivered + ' episodes delivered'); }
    if (rc.activeVsTotal && rc.activeVsTotal !== 'N/A' && !seen.has('a')) { seen.add('a'); metrics.push(rc.activeVsTotal + ' campaigns active'); }
    if (rc.kpiVsTarget && rc.kpiVsTarget !== 'N/A' && !seen.has('k')) { seen.add('k'); metrics.push(rc.kpiVsTarget); }
  }
  return scopeLines.length || metrics.length ? { scopeLines, metrics } : null;
}

// ─── Build 5 separate flag lists: pending, blocked, reasons, wins, follow-ups ──
function buildProjectFlags(project, history) {
  const pending = [];
  const blocked = [];
  const reasons = [];
  const wins = [];
  const followups = [];

  if (history && history.length) {
    const byForm = {};
    for (const h of history) {
      if (!byForm[h.form]) byForm[h.form] = h;
    }
    for (const [form, entry] of Object.entries(byForm)) {
      const info = SERVICE_INFO[form] || { icon: '📌', short: form };
      const rc = entry.richContent || {};

      if (rc.planNextWeek && rc.planNextWeek !== 'N/A' && rc.planNextWeek !== 'No Plans' && rc.planNextWeek.trim()) {
        pending.push({ service: info.short, icon: info.icon, text: rc.planNextWeek.trim() });
      }

      if (entry.blockers && entry.blockers.trim() && !FALSE_BLOCKERS.has(entry.blockers.trim().toLowerCase())) {
        blocked.push({ service: info.short, icon: info.icon, text: entry.blockers.trim() });
      }

      if (rc.flags && !FALSE_BLOCKERS.has(rc.flags.trim().toLowerCase())) {
        const noteText = (rc.notes && rc.notes !== 'N/A' && rc.notes !== 'No updates') ? rc.notes.trim() : null;
        reasons.push({
          service: info.short, icon: info.icon,
          flag: rc.flags.trim(),
          explanation: noteText
        });
      }

      // What's working well
      if (rc.completedThisWeek && rc.completedThisWeek !== 'N/A' && rc.completedThisWeek.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.completedThisWeek.trim() });
      } else if (rc.contentDelivered && rc.contentDelivered !== 'N/A' && rc.contentDelivered.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.contentDelivered.trim() });
      } else if (rc.deliveryThisWeek && rc.deliveryThisWeek !== 'N/A' && rc.deliveryThisWeek.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.deliveryThisWeek.trim() });
      }
      if (rc.conversions && rc.conversions !== 'N/A' && rc.conversions.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: `Conversions: ${rc.conversions.trim()}` });
      }
      if (rc.meetingsHeld && rc.meetingsHeld !== 'N/A' && rc.meetingsHeld.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: `Meetings held: ${rc.meetingsHeld.trim()}` });
      }
      if (rc.renewalsUpsells && rc.renewalsUpsells !== 'N/A' && rc.renewalsUpsells.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.renewalsUpsells.trim().substring(0, 120) });
      }
      if (rc.satisfactionSignals && rc.satisfactionSignals !== 'N/A' && !rc.satisfactionSignals.toLowerCase().startsWith('no negative')) {
        wins.push({ service: info.short, icon: info.icon, text: rc.satisfactionSignals.trim().substring(0, 120) });
      }

      // Needs follow-up
      const notesLower = (rc.notes || '').toLowerCase();
      if (notesLower.includes('follow up') || notesLower.includes('follow-up') || notesLower.includes('followup') || notesLower.includes('awaiting') || notesLower.includes('waiting for') || notesLower.includes('pending from')) {
        followups.push({ service: info.short, icon: info.icon, text: rc.notes.trim().substring(0, 150) });
      }
      if (rc.contentStatus && rc.contentStatus !== 'N/A' && (rc.contentStatus.toLowerCase().includes('awaiting') || rc.contentStatus.toLowerCase().includes('pending') || rc.contentStatus.toLowerCase().includes('approval'))) {
        followups.push({ service: info.short, icon: info.icon, text: rc.contentStatus.trim().substring(0, 120) });
      }
    }
  }

  const uniqueBlocked = []; const seenB = new Set();
  for (const b of blocked) { const k = b.text.substring(0, 30); if (!seenB.has(k)) { seenB.add(k); uniqueBlocked.push(b); } }
  const uniqueReasons = []; const seenR = new Set();
  for (const r of reasons) { const k = r.flag.substring(0, 30); if (!seenR.has(k)) { seenR.add(k); uniqueReasons.push(r); } }
  const uniqueWins = []; const seenW = new Set();
  for (const w of wins) { const k = w.service + ':' + w.text.substring(0, 30); if (!seenW.has(k)) { seenW.add(k); uniqueWins.push(w); } }
  const uniqueFollowups = []; const seenF = new Set();
  for (const f of followups) { const k = f.service + ':' + f.text.substring(0, 30); if (!seenF.has(k)) { seenF.add(k); uniqueFollowups.push(f); } }

  return { pending, blocked: uniqueBlocked, reasons: uniqueReasons, wins: uniqueWins.slice(0, 4), followups: uniqueFollowups.slice(0, 3) };
}

// ═══════════════════════════════════════════════════════════════════════════
// PROJECT DETAIL
// ═══════════════════════════════════════════════════════════════════════════
function ProjectDetail() {
  const { projectName } = useParams();
  const { data, loading, error } = useInsights();

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState />;
  const project = data.projects?.find(p => p.project_name === decodeURIComponent(projectName));
  if (!project) return <NotFound name={projectName} />;

  const cfg = STATUS[project.overall_status] || STATUS['Unknown'];
  const summary = (project.ai_summary || project.summary || project.latest_update || '').replace(/^No updates\.?\s*/i, '');
  const scope = buildScope(project.history);
  const services = buildServices(project.history);
  const { pending, blocked, reasons, wins, followups } = buildProjectFlags(project, project.history);
  const lastDate = project.last_update ? fmtDate(project.last_update) : '';

  const timeline = (project.history || [])
    .filter(h => h.date && !isNaN(new Date(h.date).getTime()))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <header style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '12px 24px', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <Link to="/" style={{ fontSize: 13, color: '#6366f1', textDecoration: 'none', fontWeight: 500 }}>← All Projects</Link>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '24px' }}>
        {/* ─── Project Header ─────────────────────────────────── */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111', margin: 0 }}>{project.project_name}</h1>
              <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0 0' }}>
                Managed by <strong style={{ color: '#374151' }}>{project.account_manager}</strong>
                {lastDate && <> · Updated {lastDate}</>}
              </p>
            </div>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20,
              fontSize: 14, fontWeight: 600, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`
            }}>
              {cfg.icon} {project.overall_status === 'At Risk' ? 'Needs Attention' : project.overall_status}
            </span>
          </div>
        </div>

        {/* ─── What We're Doing ─────────────────────────────── */}
        {services.length > 0 && (
          <Section title="What We're Doing" subtitle="The services IncX provides for this client" icon="🎯" accent="purple">
            <div style={{ display: 'grid', gridTemplateColumns: services.length > 2 ? '1fr 1fr' : '1fr', gap: 12 }}>
              {services.map(s => (
                <div key={s.form} style={{ background: 'white', borderRadius: 8, padding: 14, border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {s.info.icon} {s.info.short}
                  </div>
                  <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, marginBottom: 6 }}>{s.concrete}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    {s.totalUpdates} weekly update{s.totalUpdates !== 1 ? 's' : ''}
                    {s.tool ? ` · Tool: ${s.tool}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ─── Are We On Track? ──────────────────────────────── */}
        <Section title="Are We On Track?" icon={cfg.icon} accent={project.overall_status === 'On Track' ? 'green' : project.overall_status === 'At Risk' ? 'red' : 'gray'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: summary ? 12 : 0 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: cfg.color }}>
              {project.overall_status === 'At Risk' ? '⚠️ This project needs attention' : project.overall_status === 'On Track' ? '✅ Going well' : project.overall_status === 'Delayed' ? '🔴 Delayed' : '⚪ Status unclear'}
            </span>
          </div>
          {summary && summary !== 'No updates available' && (
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{summary}</p>
          )}
        </Section>

        {/* ─── 5-COLUMN: Pending | Blocked | Why Delayed | Working Well | Follow-Up ─── */}
        {(pending.length > 0 || blocked.length > 0 || reasons.length > 0 || wins.length > 0 || followups.length > 0) && (() => {
          const cols = [pending.length > 0, blocked.length > 0, reasons.length > 0, wins.length > 0, followups.length > 0].filter(Boolean).length;
          const gridCols = cols >= 4 ? '1fr 1fr 1fr 1fr' : cols === 3 ? '1fr 1fr 1fr' : cols === 2 ? '1fr 1fr' : '1fr';
          return (
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 12, marginBottom: 16 }}>
            {pending.length > 0 && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>📋 What's Pending</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Planned actions and next steps</div>
                {pending.map((p, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 8, padding: '10px 12px', marginBottom: i < pending.length - 1 ? 8 : 0, border: '1px solid #dbeafe' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>{p.icon} {p.service}</div>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{p.text.length > 120 ? p.text.slice(0, 120) + '…' : p.text}</div>
                  </div>
                ))}
              </div>
            )}
            {blocked.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>🚫 What's Blocked</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Tasks or work that is stuck</div>
                {blocked.map((b, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 8, padding: '10px 12px', marginBottom: i < blocked.length - 1 ? 8 : 0, border: '1px solid #fecaca' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>{b.icon} {b.service}</div>
                    <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.5 }}>{b.text}</div>
                  </div>
                ))}
              </div>
            )}
            {reasons.length > 0 && (
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#c2410c', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>💡 Why It's Delayed</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>The root cause and explanation</div>
                {reasons.map((r, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 8, padding: '10px 12px', marginBottom: i < reasons.length - 1 ? 8 : 0, border: '1px solid #fed7aa' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#c2410c', marginBottom: 4 }}>{r.icon} {r.service}: {r.flag}</div>
                    {r.explanation && (
                      <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>{r.explanation.length > 200 ? r.explanation.slice(0, 200) + '…' : r.explanation}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {wins.length > 0 && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>✅ What's Working Well</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Wins, completions & positive signals</div>
                {wins.slice(0, 3).map((w, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 8, padding: '10px 12px', marginBottom: i < Math.min(wins.length, 3) - 1 ? 8 : 0, border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 4 }}>{w.icon} {w.service}</div>
                    <div style={{ fontSize: 13, color: '#15803d', lineHeight: 1.5 }}>{w.text.length > 120 ? w.text.slice(0, 120) + '…' : w.text}</div>
                  </div>
                ))}
              </div>
            )}
            {followups.length > 0 && (
              <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#7e22ce', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>🔔 Needs Follow-Up</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Items requiring action or response</div>
                {followups.map((f, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 8, padding: '10px 12px', marginBottom: i < followups.length - 1 ? 8 : 0, border: '1px solid #e9d5ff' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#7e22ce', marginBottom: 4 }}>{f.icon} {f.service}</div>
                    <div style={{ fontSize: 13, color: '#581c87', lineHeight: 1.5 }}>{f.text.length > 120 ? f.text.slice(0, 120) + '…' : f.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })()}


        {/* ─── Scope & Targets ──────────────────────────────── */}
        {scope && (
          <Section title="Scope & Targets" subtitle="What we committed to deliver" icon="📋" accent="blue">
            {scope.scopeLines.length > 0 && (
              <div style={{ marginBottom: scope.metrics.length > 0 ? 12 : 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 6 }}>Deliverables:</div>
                {scope.scopeLines.map((line, i) => (
                  <div key={i} style={{ background: '#eff6ff', borderRadius: 6, padding: '8px 12px', marginBottom: 4, fontSize: 13, color: '#1e3a5f', lineHeight: 1.5 }}>
                    {line}
                  </div>
                ))}
              </div>
            )}
            {scope.metrics.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {scope.metrics.map((m, i) => (
                  <span key={i} style={{ padding: '6px 12px', borderRadius: 6, background: '#dbeafe', fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
                    📊 {m}
                  </span>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* ─── Service-by-Service Details ────────────────────── */}
        {services.length > 0 && (
          <Section title="Service-by-Service Details" subtitle="What each team is doing and their latest activity" icon="📊">
            {services.map(s => {
              const scfg = STATUS[s.status] || STATUS['Unknown'];
              return (
                <div key={s.form} style={{
                  background: 'white', border: `1px solid ${scfg.border}`, borderLeft: `4px solid ${scfg.color}`,
                  borderRadius: 8, padding: 16, marginBottom: 12
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{s.info.icon} {s.info.short}</span>
                      <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 10, background: scfg.bg, color: scfg.color, fontWeight: 600 }}>
                        {s.status === 'At Risk' ? 'Needs Attention' : s.status}
                      </span>
                    </div>
                    {s.lastUpdate && <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtShort(s.lastUpdate)}</span>}
                  </div>

                  {/* What was done */}
                  {s.activity && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>WHAT WAS DONE</div>
                      <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: 0 }}>{s.activity.length > 200 ? s.activity.slice(0, 200) + '…' : s.activity}</p>
                    </div>
                  )}

                  {/* Metrics */}
                  {s.metrics.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: s.blockedText || s.reasonFlag || s.nextPlan ? 8 : 0 }}>
                      {s.metrics.map((m, i) => (
                        <span key={i} style={{ padding: '4px 10px', borderRadius: 6, background: '#f3f4f6', fontSize: 12, color: '#374151' }}>
                          {m.l}: <strong>{m.v}</strong>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* ── Inside service: separate blocked / reason / pending ── */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {s.blockedText && (
                      <div style={{ flex: '1 1 200px', background: '#fef2f2', borderRadius: 6, padding: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', letterSpacing: 0.5, marginBottom: 2 }}>🚫 BLOCKED</div>
                        <div style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.4 }}>{s.blockedText}</div>
                      </div>
                    )}
                    {s.reasonFlag && (
                      <div style={{ flex: '1 1 200px', background: '#fff7ed', borderRadius: 6, padding: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#c2410c', letterSpacing: 0.5, marginBottom: 2 }}>💡 WHY DELAYED</div>
                        <div style={{ fontSize: 12, color: '#9a3412', lineHeight: 1.4 }}>
                          <strong>{s.reasonFlag}</strong>
                          {s.reasonExplanation && <span> — {s.reasonExplanation.length > 120 ? s.reasonExplanation.slice(0, 120) + '…' : s.reasonExplanation}</span>}
                        </div>
                      </div>
                    )}
                    {s.nextPlan && (
                      <div style={{ flex: '1 1 200px', background: '#eff6ff', borderRadius: 6, padding: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af', letterSpacing: 0.5, marginBottom: 2 }}>📋 NEXT WEEK</div>
                        <div style={{ fontSize: 12, color: '#1e3a5f', lineHeight: 1.4 }}>{s.nextPlan.length > 150 ? s.nextPlan.slice(0, 150) + '…' : s.nextPlan}</div>
                      </div>
                    )}
                  </div>

                  {/* Engagement details */}
                  {s.engagement && (
                    <div style={{ background: '#f8fafc', borderRadius: 6, padding: 10, marginTop: 8, fontSize: 12, color: '#4b5563', lineHeight: 1.5 }}>
                      {s.engagement.length > 200 ? s.engagement.slice(0, 200) + '…' : s.engagement}
                    </div>
                  )}
                </div>
              );
            })}
          </Section>
        )}

        {/* ─── Recent Timeline ─────────────────────────────────── */}
        {timeline.length > 0 && (
          <Section title={`Recent Updates (${(project.history || []).length} total)`} icon="📅">
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: '#e5e7eb' }} />
              {timeline.map((entry, i) => {
                const ecfg = STATUS[norm(entry.status)] || STATUS['Unknown'];
                const smeta = SERVICE_INFO[entry.form] || { icon: '📌', short: entry.form };
                const rc = entry.richContent || {};
                let text = entry.summary || '';
                if (!text || text === 'No updates' || text === 'No updates provided') {
                  const parts = [];
                  if (rc.notes && rc.notes !== 'N/A' && rc.notes !== 'No updates') parts.push(rc.notes);
                  if (rc.activity && rc.activity !== 'N/A' && rc.activity !== 'No activities happened') parts.push(rc.activity);
                  if (rc.completedThisWeek && rc.completedThisWeek !== 'N/A') parts.push(rc.completedThisWeek);
                  text = parts.join(' · ');
                }
                const week = entry.week || fmtShort(entry.date);
                return (
                  <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: ecfg.bg, border: `2px solid ${ecfg.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0, zIndex: 1 }}>
                      {ecfg.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{week} · {smeta.icon} {smeta.short}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: ecfg.bg, color: ecfg.color, fontWeight: 600 }}>
                          {norm(entry.status) === 'At Risk' ? 'Needs Attention' : norm(entry.status)}
                        </span>
                      </div>
                      {text && <p style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5, margin: 0 }}>{text.length > 200 ? text.slice(0, 200) + '…' : text}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <Link to={`/project/${encodeURIComponent(project.project_name)}/history`} style={{ color: '#6366f1', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                View all {project.history?.length || 0} updates →
              </Link>
            </div>
          </Section>
        )}

        {/* ─── Also Known As ───────────────────────────────────── */}
        {project.all_names?.length > 1 && (
          <Section title="Also Known As" icon="🏷️">
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px 0' }}>This project also appears in the data as:</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {project.all_names.filter(n => n !== project.project_name).map(name => (
                <span key={name} style={{ padding: '4px 12px', borderRadius: 6, background: '#f3f4f6', fontSize: 13, color: '#4b5563' }}>{name}</span>
              ))}
            </div>
          </Section>
        )}
      </main>
    </div>
  );
}

function Section({ title, subtitle, icon, accent, children }) {
  const colors = {
    red:    { bg: '#fef2f2', border: '#fecaca', title: '#991b1b' },
    green:  { bg: '#f0fdf4', border: '#bbf7d0', title: '#166534' },
    blue:   { bg: '#eff6ff', border: '#bfdbfe', title: '#1e40af' },
    purple: { bg: '#faf5ff', border: '#e9d5ff', title: '#6d28d9' },
    gray:   { bg: '#f9fafb', border: '#e5e7eb', title: '#111' }
  };
  const c = colors[accent] || colors.gray;
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: c.title, margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon} {title}
      </h2>
      {subtitle && <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px 0' }}>{subtitle}</p>}
      {!subtitle && <div style={{ marginBottom: 12 }} />}
      {children}
    </div>
  );
}

function Loading() {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#9ca3af' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 8 }}>IncX<span style={{ color: '#6366f1' }}>Ops</span></div>
      <div>Loading...</div>
    </div>
  </div>;
}
function ErrorState({ message }) {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>Something went wrong</div>
      <div style={{ fontSize: 14 }}>{message}</div>
    </div>
  </div>;
}
function EmptyState() {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data</div>;
}
function NotFound({ name }) {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>Project not found</div>
      <Link to="/" style={{ color: '#6366f1', textDecoration: 'none' }}>← Back to all projects</Link>
    </div>
  </div>;
}

export default ProjectDetail;